/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License. See License.txt in the project root for
 * license information.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is
 * regenerated.
 */
import { __assign } from "tslib";
import * as msRest from "@azure/ms-rest-js";
import * as Mappers from "../models/labPlansMappers";
import * as Parameters from "../models/parameters";
/** Class representing a LabPlans. */
var LabPlans = /** @class */function () {
  /**
   * Create a LabPlans.
   * @param {LabServicesClientContext} client Reference to the service client.
   */
  function LabPlans(client) {
    this.client = client;
  }
  LabPlans.prototype.listBySubscription = function (options, callback) {
    return this.client.sendOperationRequest({
      options: options
    }, listBySubscriptionOperationSpec, callback);
  };
  LabPlans.prototype.listByResourceGroup = function (resourceGroupName, options, callback) {
    return this.client.sendOperationRequest({
      resourceGroupName: resourceGroupName,
      options: options
    }, listByResourceGroupOperationSpec, callback);
  };
  LabPlans.prototype.get = function (resourceGroupName, labPlanName, options, callback) {
    return this.client.sendOperationRequest({
      resourceGroupName: resourceGroupName,
      labPlanName: labPlanName,
      options: options
    }, getOperationSpec, callback);
  };
  /**
   * Operation to create or update a Lab Plan resource.
   * @summary Updates or creates a Lab Plan resource.
   * @param body The request body.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labPlanName The name of the lab plan that uniquely identifies it within containing
   * resource group. Used in resource URIs and in UI.
   * @param [options] The optional parameters
   * @returns Promise<Models.LabPlansCreateOrUpdateResponse>
   */
  LabPlans.prototype.createOrUpdate = function (body, resourceGroupName, labPlanName, options) {
    return this.beginCreateOrUpdate(body, resourceGroupName, labPlanName, options).then(function (lroPoller) {
      return lroPoller.pollUntilFinished();
    });
  };
  /**
   * Operation to update a Lab Plan resource.
   * @summary Updates a Lab Plan resource.
   * @param body The request body.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labPlanName The name of the lab plan that uniquely identifies it within containing
   * resource group. Used in resource URIs and in UI.
   * @param [options] The optional parameters
   * @returns Promise<Models.LabPlansUpdateResponse>
   */
  LabPlans.prototype.update = function (body, resourceGroupName, labPlanName, options) {
    return this.beginUpdate(body, resourceGroupName, labPlanName, options).then(function (lroPoller) {
      return lroPoller.pollUntilFinished();
    });
  };
  /**
   * Operation to delete a Lab Plan resource. Deleting a lab plan does not delete labs associated
   * with a lab plan, nor does it delete shared images added to a gallery via the lab plan permission
   * container.
   * @summary Deletes a Lab Plan resource.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labPlanName The name of the lab plan that uniquely identifies it within containing
   * resource group. Used in resource URIs and in UI.
   * @param [options] The optional parameters
   * @returns Promise<msRest.RestResponse>
   */
  LabPlans.prototype.deleteMethod = function (resourceGroupName, labPlanName, options) {
    return this.beginDeleteMethod(resourceGroupName, labPlanName, options).then(function (lroPoller) {
      return lroPoller.pollUntilFinished();
    });
  };
  /**
   * Saves an image from a lab VM to the attached shared image gallery.
   * @summary Save an image from a lab VM to the attached shared image gallery.
   * @param body The request body.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labPlanName The name of the lab plan that uniquely identifies it within containing
   * resource group. Used in resource URIs and in UI.
   * @param [options] The optional parameters
   * @returns Promise<msRest.RestResponse>
   */
  LabPlans.prototype.saveImage = function (body, resourceGroupName, labPlanName, options) {
    return this.beginSaveImage(body, resourceGroupName, labPlanName, options).then(function (lroPoller) {
      return lroPoller.pollUntilFinished();
    });
  };
  /**
   * Operation to create or update a Lab Plan resource.
   * @summary Updates or creates a Lab Plan resource.
   * @param body The request body.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labPlanName The name of the lab plan that uniquely identifies it within containing
   * resource group. Used in resource URIs and in UI.
   * @param [options] The optional parameters
   * @returns Promise<msRestAzure.LROPoller>
   */
  LabPlans.prototype.beginCreateOrUpdate = function (body, resourceGroupName, labPlanName, options) {
    return this.client.sendLRORequest({
      body: body,
      resourceGroupName: resourceGroupName,
      labPlanName: labPlanName,
      options: options
    }, beginCreateOrUpdateOperationSpec, options);
  };
  /**
   * Operation to update a Lab Plan resource.
   * @summary Updates a Lab Plan resource.
   * @param body The request body.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labPlanName The name of the lab plan that uniquely identifies it within containing
   * resource group. Used in resource URIs and in UI.
   * @param [options] The optional parameters
   * @returns Promise<msRestAzure.LROPoller>
   */
  LabPlans.prototype.beginUpdate = function (body, resourceGroupName, labPlanName, options) {
    return this.client.sendLRORequest({
      body: body,
      resourceGroupName: resourceGroupName,
      labPlanName: labPlanName,
      options: options
    }, beginUpdateOperationSpec, options);
  };
  /**
   * Operation to delete a Lab Plan resource. Deleting a lab plan does not delete labs associated
   * with a lab plan, nor does it delete shared images added to a gallery via the lab plan permission
   * container.
   * @summary Deletes a Lab Plan resource.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labPlanName The name of the lab plan that uniquely identifies it within containing
   * resource group. Used in resource URIs and in UI.
   * @param [options] The optional parameters
   * @returns Promise<msRestAzure.LROPoller>
   */
  LabPlans.prototype.beginDeleteMethod = function (resourceGroupName, labPlanName, options) {
    return this.client.sendLRORequest({
      resourceGroupName: resourceGroupName,
      labPlanName: labPlanName,
      options: options
    }, beginDeleteMethodOperationSpec, options);
  };
  /**
   * Saves an image from a lab VM to the attached shared image gallery.
   * @summary Save an image from a lab VM to the attached shared image gallery.
   * @param body The request body.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labPlanName The name of the lab plan that uniquely identifies it within containing
   * resource group. Used in resource URIs and in UI.
   * @param [options] The optional parameters
   * @returns Promise<msRestAzure.LROPoller>
   */
  LabPlans.prototype.beginSaveImage = function (body, resourceGroupName, labPlanName, options) {
    return this.client.sendLRORequest({
      body: body,
      resourceGroupName: resourceGroupName,
      labPlanName: labPlanName,
      options: options
    }, beginSaveImageOperationSpec, options);
  };
  LabPlans.prototype.listBySubscriptionNext = function (nextPageLink, options, callback) {
    return this.client.sendOperationRequest({
      nextPageLink: nextPageLink,
      options: options
    }, listBySubscriptionNextOperationSpec, callback);
  };
  LabPlans.prototype.listByResourceGroupNext = function (nextPageLink, options, callback) {
    return this.client.sendOperationRequest({
      nextPageLink: nextPageLink,
      options: options
    }, listByResourceGroupNextOperationSpec, callback);
  };
  return LabPlans;
}();
export { LabPlans };
// Operation Specifications
var serializer = new msRest.Serializer(Mappers);
var listBySubscriptionOperationSpec = {
  httpMethod: "GET",
  path: "subscriptions/{subscriptionId}/providers/Microsoft.LabServices/labPlans",
  urlParameters: [Parameters.subscriptionId],
  queryParameters: [Parameters.apiVersion, Parameters.filter],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {
      bodyMapper: Mappers.PagedLabPlans
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var listByResourceGroupOperationSpec = {
  httpMethod: "GET",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labPlans",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName],
  queryParameters: [Parameters.apiVersion],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {
      bodyMapper: Mappers.PagedLabPlans
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var getOperationSpec = {
  httpMethod: "GET",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labPlans/{labPlanName}",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName, Parameters.labPlanName],
  queryParameters: [Parameters.apiVersion],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {
      bodyMapper: Mappers.LabPlan
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var beginCreateOrUpdateOperationSpec = {
  httpMethod: "PUT",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labPlans/{labPlanName}",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName, Parameters.labPlanName],
  queryParameters: [Parameters.apiVersion],
  headerParameters: [Parameters.acceptLanguage],
  requestBody: {
    parameterPath: "body",
    mapper: __assign(__assign({}, Mappers.LabPlan), {
      required: true
    })
  },
  responses: {
    200: {
      bodyMapper: Mappers.LabPlan
    },
    201: {
      bodyMapper: Mappers.LabPlan
    },
    202: {
      bodyMapper: Mappers.LabPlan
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var beginUpdateOperationSpec = {
  httpMethod: "PATCH",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labPlans/{labPlanName}",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName, Parameters.labPlanName],
  queryParameters: [Parameters.apiVersion],
  headerParameters: [Parameters.acceptLanguage],
  requestBody: {
    parameterPath: "body",
    mapper: __assign(__assign({}, Mappers.LabPlanUpdate), {
      required: true
    })
  },
  responses: {
    200: {
      bodyMapper: Mappers.LabPlan
    },
    202: {
      bodyMapper: Mappers.LabPlan
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var beginDeleteMethodOperationSpec = {
  httpMethod: "DELETE",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labPlans/{labPlanName}",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName, Parameters.labPlanName],
  queryParameters: [Parameters.apiVersion],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {},
    202: {},
    204: {},
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var beginSaveImageOperationSpec = {
  httpMethod: "POST",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labPlans/{labPlanName}/saveImage",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName, Parameters.labPlanName],
  queryParameters: [Parameters.apiVersion],
  headerParameters: [Parameters.acceptLanguage],
  requestBody: {
    parameterPath: "body",
    mapper: __assign(__assign({}, Mappers.SaveImageBody), {
      required: true
    })
  },
  responses: {
    200: {},
    202: {},
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var listBySubscriptionNextOperationSpec = {
  httpMethod: "GET",
  baseUrl: "https://management.azure.com",
  path: "{nextLink}",
  urlParameters: [Parameters.nextPageLink],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {
      bodyMapper: Mappers.PagedLabPlans
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var listByResourceGroupNextOperationSpec = {
  httpMethod: "GET",
  baseUrl: "https://management.azure.com",
  path: "{nextLink}",
  urlParameters: [Parameters.nextPageLink],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {
      bodyMapper: Mappers.PagedLabPlans
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};